/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
import React, { useContext, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Loader } from 'semantic-ui-react';
import PropTypes from 'prop-types';

import { ViewSignatureWrapper, SignatureWrapper, ButtonWrapper, CanvasWrapper } from './signature.styled';
import { setUserInputOnCtrl, startUpload, endUpload } from '../../../form/reducer/form';
import { whenSideBySide, assignFormDesignToCSSVars } from '../fields-customise.style';
import { acceptSignature, publicBlobReaderApi } from '../../../form/utils';
import { FormContext } from '../../../customise/contexts/form-context';
import SpecialPropsIndicators from '../props-Indicator/special-props';
import { ctrlDefaults, toHex } from '../../../../shared/form-lib';
import { selectCtrlIdLabelMap } from '../../selectors';
import SignaturePad from 'react-signature-pad-wrapper';
import { ControlPropType } from '../../ctrl_proptype';
import {
  ValidationWarning,
  MainfieldWrapper,
  SubtextWrapper,
  FieldWrapper,
  LabelWrapper,
  Field,
} from '../../common-field-styles';

const OFFLINE_ERROR_MESSAGE = 'You cannot add a signature while offline';

const Signature = ({ ctrl, formDesign }) => {
  const dispatch = useDispatch();
  const signaturePadRef = useRef();
  const { inBuilder, inForm, currentPageNo } = useContext(FormContext);
  const { userIsOnline, userCustomTexts } = useSelector((state) => state.form);

  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const fieldLabel = ctrlIdLabelMap?.[ctrl.internal_id];

  const userIsOffline = !userIsOnline;
  const [signature, setSignature] = useState('');
  const [viewSignature, setViewSignature] = useState(!!ctrl.value);
  const [ctrlValidationError, setCtrlValidationError] = useState(ctrl._validationError ?? '');
  const [uploadState, setUploadState] = useState({
    uploading: false,
    uploaded: false,
    uploadedFileName: ctrl.value ? ctrl.value.filename : '',
  });
  const isUploading = uploadState.uploading;

  useEffect(() => {
    if (ctrl.value) {
      const blobPath = ctrl.value.path;
      publicBlobReaderApi(blobPath, setSignature);
    }

    return () => {
      setSignature('');
    };
  }, []);

  useEffect(() => {
    // For E-Sign pdf document rendering
    if (inForm) saveSignatureDataURL(signature);
  }, [signature]);

  useEffect(() => {
    setCtrlValidationError(ctrl._validationError);
  }, [ctrl._validationError]);

  const saveEmptySignature = () => {
    const emptyValue = '';
    dispatch(
      setUserInputOnCtrl({
        newValue: emptyValue,
        property: 'value',
        page: currentPageNo - 1,
        ctrlId: ctrl.internal_id,
      })
    );
  };

  const saveUpdatedCtrl = (newValue) => {
    setViewSignature(true);
    dispatch(
      setUserInputOnCtrl({
        newValue,
        property: 'value',
        page: currentPageNo - 1,
        ctrlId: ctrl.internal_id,
        isValid: true,
      })
    );
  };

  const saveSignatureDataURL = (newValue) => {
    dispatch(
      setUserInputOnCtrl({
        newValue,
        property: 'dataURL',
        page: currentPageNo - 1,
        ctrlId: ctrl.internal_id,
        isValid: true,
      })
    );
  };

  const beginUpload = () => {
    dispatch(startUpload());
  };

  const stopUpload = () => {
    dispatch(endUpload());
  };

  const handleUseSignature = () => {
    if (!inForm || userIsOffline) return;
    if (signaturePadRef.current.isEmpty()) {
      saveEmptySignature();
      setUploadState((prev) => ({
        ...prev,
        uploaded: false,
        uploadedFileName: '',
      }));
    } else {
      const currentSignature = signaturePadRef.current.toDataURL('image/png');
      setSignature(currentSignature);
      acceptSignature({
        ctrl,
        stopUpload,
        beginUpload,
        setUploadState,
        saveUpdatedCtrl,
        setCtrlValidationError,
        isOffline: userIsOffline,
        dataURL: currentSignature
      });
    }
  };

  const handleClearSignature = () => {
    if (!inForm) return;
    signaturePadRef?.current?.clear();
    setViewSignature(false);
    saveEmptySignature();
    setUploadState((prev) => ({
      ...prev,
      uploaded: false,
      uploadedFileName: '',
    }));
  };

  const buttonStyles = inBuilder
    ? undefined
    : {
        '--btn-color': toHex(formDesign.design.secondary_button_color),
        '--btn-bag': toHex(formDesign.design.secondary_button_background_color),
      };

  return (
    <MainfieldWrapper style={assignFormDesignToCSSVars(inBuilder, formDesign)}>
      <FieldWrapper>
        {(userIsOffline || ctrlValidationError) && (
          <ValidationWarning className="ui bottom pointing basic label red">
            {userIsOffline ? OFFLINE_ERROR_MESSAGE : ctrlValidationError}
          </ValidationWarning>
        )}

        <Field className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
          <LabelWrapper
            notInBuilder={!inBuilder}
            required={ctrl.props.required}
            className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
            <label dangerouslySetInnerHTML={{ __html: fieldLabel || ctrlDefaults()[ctrl.type] }} />
            <SpecialPropsIndicators ctrl={ctrl} />
          </LabelWrapper>

          <SignatureWrapper
            notInBuilder={!inBuilder}
            id={`fpl-${ctrl.internal_id}`}
            className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
            <CanvasWrapper height={ctrl.props.height} width={ctrl.props.width} disabled={!inForm || userIsOffline}>
              {viewSignature ? (
                <ViewSignatureWrapper>
                  <img src={signature} alt="signature" />
                </ViewSignatureWrapper>
              ) : (
                <SignaturePad ref={signaturePadRef} accept="accept" clear="clear" height={100} />
              )}
            </CanvasWrapper>

            <ButtonWrapper style={buttonStyles}>
              <button
                disabled={isUploading}
                onClick={handleClearSignature}
                className={`clear ${!inBuilder ? 'not-in-builder' : ''}`}>
                {userCustomTexts.resets || 'Clear'}
              </button>

              <button
                onClick={handleUseSignature}
                disabled={isUploading || viewSignature}
                className={`accept ${!inBuilder ? 'not-in-builder' : ''} ${viewSignature ? 'accepted' : ''}`}>
                {viewSignature ? (
                  <Icon name="checkmark" />
                ) : isUploading ? (
                  <Loader active size="tiny" inline="centered" />
                ) : (
                  userCustomTexts.accept || 'Use'
                )}
              </button>
            </ButtonWrapper>

            <SubtextWrapper id="descriptive text" notInBuilder={!inBuilder}>
            {ctrl.props.subtext}
          </SubtextWrapper>
          </SignatureWrapper>
        </Field>
      </FieldWrapper>
    </MainfieldWrapper>
  );
};

Signature.defaultProps = {
  formDesign: null,
};

Signature.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};

export default Signature;
